import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { AlertTriangle, Wallet } from "lucide-react";

interface Props {
  orgId: string | null;
  spentUsd: number;
}

export function BudgetCard({ orgId, spentUsd }: Props) {
  const qc = useQueryClient();
  const [budget, setBudget] = useState<string>("50");
  const [threshold, setThreshold] = useState<string>("80");
  const [email, setEmail] = useState<string>("");
  const [active, setActive] = useState(true);

  const { data: row } = useQuery({
    queryKey: ["ai-budget", orgId],
    queryFn: async () => {
      if (!orgId || orgId === "all") return null;
      const { data } = await supabase.from("ai_budgets").select("*").eq("org_id", orgId).maybeSingle();
      return data;
    },
    enabled: !!orgId && orgId !== "all",
  });

  useEffect(() => {
    if (row) {
      setBudget(String(row.monthly_budget_usd ?? 50));
      setThreshold(String(row.alert_threshold_pct ?? 80));
      setEmail(row.notify_email ?? "");
      setActive(row.active ?? true);
    }
  }, [row]);

  const save = useMutation({
    mutationFn: async () => {
      if (!orgId || orgId === "all") throw new Error("Selecione uma organização específica");
      const payload = {
        org_id: orgId,
        monthly_budget_usd: Number(budget),
        alert_threshold_pct: Number(threshold),
        notify_email: email || null,
        active,
      };
      const { error } = await supabase.from("ai_budgets").upsert(payload, { onConflict: "org_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Orçamento salvo");
      qc.invalidateQueries({ queryKey: ["ai-budget", orgId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!orgId || orgId === "all") {
    return (
      <Card>
        <CardHeader><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><Wallet className="w-4 h-4" /> Orçamento Mensal</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-muted-foreground">Selecione uma organização específica para configurar orçamento.</p></CardContent>
      </Card>
    );
  }

  const budgetNum = Number(budget) || 0;
  const pct = budgetNum > 0 ? Math.min(100, (spentUsd / budgetNum) * 100) : 0;
  const overThreshold = pct >= Number(threshold);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Wallet className="w-4 h-4" /> Orçamento Mensal de IA
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-muted-foreground">Gasto este mês</span>
            <span className={`font-mono font-semibold ${overThreshold ? "text-destructive" : "text-foreground"}`}>
              ${spentUsd.toFixed(4)} / ${budgetNum.toFixed(2)} ({pct.toFixed(1)}%)
            </span>
          </div>
          <Progress value={pct} className={overThreshold ? "[&>div]:bg-destructive" : ""} />
          {overThreshold && active && (
            <p className="flex items-center gap-1 text-xs text-destructive mt-2">
              <AlertTriangle className="w-3 h-3" /> Limite de alerta atingido
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Orçamento (USD/mês)</Label>
            <Input type="number" step="0.01" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Alertar em (%)</Label>
            <Input type="number" min="1" max="100" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
          </div>
        </div>
        <div>
          <Label className="text-xs">Email para alertas</Label>
          <Input type="email" placeholder="alerts@empresa.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch checked={active} onCheckedChange={setActive} />
            <Label className="text-sm">Alertas ativos</Label>
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
